import React, { useState, useEffect } from 'react';
import {
  FileText, Download, Users, IndianRupee, Calendar, Clock,
  TrendingUp, Building2, ChevronDown, Filter, FileSpreadsheet,
  FileDown, Printer, Mail, RefreshCw, Search, X, Check,
  BarChart3, PieChart, Activity, AlertCircle, CheckCircle2
} from 'lucide-react';
import { employeeAPI, establishmentAPI, attendanceAPI, wagesAPI, leaveAPI, payrollAPI, reportsAPI } from '../api/apiService';
import { useAuth, useActiveEstId } from './AuthContext';
import * as XLSX from 'xlsx';
import DateInput from './DateInput';
import './UpdateEmployee.css';
import './Reports.css';

const ReportsHub = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  // State management
  const [selectedCategory, setSelectedCategory] = useState('workforce');
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || 'all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const [establishments, setEstablishments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exportFormat, setExportFormat] = useState('excel');

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Industry-standard report categories
  const reportCategories = [
    {
      id: 'workforce',
      name: 'Workforce Reports',
      icon: Users,
      color: '#1a73e8',
      description: 'Employee headcount, demographics, and composition'
    },
    {
      id: 'attendance',
      name: 'Attendance & Time',
      icon: Clock,
      color: '#f59e0b',
      description: 'Attendance, absences, overtime, and time tracking'
    },
    {
      id: 'leave',
      name: 'Leave Management',
      icon: Calendar,
      color: '#10b981',
      description: 'Leave balances, approvals, and history'
    },
    {
      id: 'payroll',
      name: 'Payroll Reports',
      icon: IndianRupee,
      color: '#8b5cf6',
      description: 'Salary, deductions, taxes, and payment summaries'
    },
    {
      id: 'compliance',
      name: 'Compliance & Statutory',
      icon: FileText,
      color: '#ef4444',
      description: 'PF, ESI, PT, and statutory compliance reports'
    },
    {
      id: 'analytics',
      name: 'HR Analytics',
      icon: TrendingUp,
      color: '#06b6d4',
      description: 'Turnover, retention, and workforce analytics'
    }
  ];

  // Comprehensive report types by category
  const reportTypes = {
    workforce: [
      {
        id: 'headcount-summary',
        name: 'Headcount Summary',
        description: 'Total employees by organization and department',
        fields: ['organization', 'department'],
        exportFormats: ['excel', 'pdf', 'csv']
      },
      {
        id: 'employee-list',
        name: 'Employee Master List',
        description: 'Complete employee directory with all details',
        fields: ['organization', 'department', 'status'],
        exportFormats: ['excel', 'pdf', 'csv']
      },
      {
        id: 'new-joiners',
        name: 'New Joiners Report',
        description: 'Employees who joined in selected period',
        fields: ['organization', 'dateRange'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'separations',
        name: 'Employee Separations',
        description: 'Employees who left in selected period',
        fields: ['organization', 'dateRange'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'demographics',
        name: 'Workforce Demographics',
        description: 'Age, gender, and diversity statistics',
        fields: ['organization'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'department-distribution',
        name: 'Department Distribution',
        description: 'Employee count by department',
        fields: ['organization'],
        exportFormats: ['excel', 'pdf', 'csv']
      }
    ],
    attendance: [
      {
        id: 'monthly-attendance',
        name: 'Monthly Attendance Register',
        description: 'Complete attendance record for the month',
        fields: ['organization', 'department', 'month', 'year'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'daily-attendance',
        name: 'Daily Attendance Report',
        description: 'Attendance for a specific date',
        fields: ['organization', 'date'],
        exportFormats: ['excel', 'pdf', 'csv']
      },
      {
        id: 'absenteeism',
        name: 'Absenteeism Report',
        description: 'Employee absences and patterns',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'overtime-report',
        name: 'Overtime Report',
        description: 'Overtime hours by employee',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf', 'csv']
      },
      {
        id: 'shift-schedule',
        name: 'Shift Schedule Report',
        description: 'Employee shift assignments',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'late-coming',
        name: 'Late Coming Report',
        description: 'Employees with late arrivals',
        fields: ['organization', 'dateRange'],
        exportFormats: ['excel', 'pdf']
      }
    ],
    leave: [
      {
        id: 'leave-balance',
        name: 'Leave Balance Report',
        description: 'Current leave balances for all employees',
        fields: ['organization', 'department'],
        exportFormats: ['excel', 'pdf', 'csv']
      },
      {
        id: 'leave-taken',
        name: 'Leave Taken Report',
        description: 'Leaves taken in selected period',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'leave-pending',
        name: 'Pending Leave Requests',
        description: 'Leave requests awaiting approval',
        fields: ['organization'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'leave-history',
        name: 'Leave History',
        description: 'Complete leave history by employee',
        fields: ['organization', 'dateRange'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'leave-trends',
        name: 'Leave Trends Analysis',
        description: 'Leave patterns and trends',
        fields: ['organization', 'year'],
        exportFormats: ['excel', 'pdf']
      }
    ],
    payroll: [
      {
        id: 'salary-register',
        name: 'Monthly Salary Register',
        description: 'Complete salary details for the month',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'payslip-bulk',
        name: 'Bulk Payslip Generation',
        description: 'Payslips for all employees',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['pdf']
      },
      {
        id: 'salary-summary',
        name: 'Salary Summary',
        description: 'Gross, deductions, and net summary',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf', 'csv']
      },
      {
        id: 'deductions-report',
        name: 'Deductions Report',
        description: 'All deductions breakdown',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'bonus-allowances',
        name: 'Bonus & Allowances Report',
        description: 'Special payments and allowances',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'bank-transfer',
        name: 'Bank Transfer Report',
        description: 'Net salary for bank transfers',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'csv']
      },
      {
        id: 'cost-center',
        name: 'Cost Center Report',
        description: 'Payroll costs by department',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf']
      }
    ],
    compliance: [
      {
        id: 'pf-report',
        name: 'PF Contribution Report',
        description: 'Employee and employer PF contributions',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf', 'csv']
      },
      {
        id: 'esi-report',
        name: 'ESI Contribution Report',
        description: 'ESI contributions and eligibility',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'pt-report',
        name: 'Professional Tax Report',
        description: 'PT deductions by state',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'tds-report',
        name: 'TDS Report',
        description: 'Tax deducted at source',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'form-16',
        name: 'Form 16 Generation',
        description: 'Annual tax certificate',
        fields: ['organization', 'year'],
        exportFormats: ['pdf']
      },
      {
        id: 'ecr-challan',
        name: 'ECR Challan',
        description: 'Electronic challan cum return for PF',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'txt']
      }
    ],
    analytics: [
      {
        id: 'turnover-rate',
        name: 'Employee Turnover Rate',
        description: 'Attrition and turnover analysis',
        fields: ['organization', 'dateRange'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'retention-analysis',
        name: 'Retention Analysis',
        description: 'Employee retention metrics',
        fields: ['organization', 'year'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'age-analysis',
        name: 'Age Analysis',
        description: 'Workforce age distribution',
        fields: ['organization'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'tenure-analysis',
        name: 'Tenure Analysis',
        description: 'Employee service length distribution',
        fields: ['organization'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'salary-benchmarking',
        name: 'Salary Benchmarking',
        description: 'Compensation analysis by role',
        fields: ['organization'],
        exportFormats: ['excel', 'pdf']
      },
      {
        id: 'productivity-metrics',
        name: 'Productivity Metrics',
        description: 'Attendance and performance indicators',
        fields: ['organization', 'month', 'year'],
        exportFormats: ['excel', 'pdf']
      }
    ]
  };

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setSelectedEstablishment(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedEstablishment && selectedEstablishment !== 'all') {
      fetchDepartments(selectedEstablishment);
    }
  }, [selectedEstablishment]);

  const fetchInitialData = async () => {
    if (lockedEstId) { setLoading(false); return; } // ClientAdmin: skip org list
    try {
      setLoading(true);
      const response = await establishmentAPI.getAll();
      setEstablishments(response.establishments || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async (estId) => {
    try {
      const response = await establishmentAPI.getDepartments(estId);
      setDepartments(response.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedReport) {
      alert('Please select a report type');
      return;
    }

    setGenerating(true);
    setReportData(null);

    try {
      // Build API parameters
      const params = {
        establishment_id: selectedEstablishment !== 'all' ? selectedEstablishment : null,
        department: selectedDepartment !== 'all' ? selectedDepartment : null,
        month: selectedMonth,
        year: selectedYear,
        start_date: dateRange.start || null,
        end_date: dateRange.end || null
      };

      // Call appropriate API based on report type
      let data;
      switch (selectedCategory) {
        case 'workforce':
          data = await generateWorkforceReport(selectedReport.id, params);
          break;
        case 'attendance':
          data = await generateAttendanceReport(selectedReport.id, params);
          break;
        case 'leave':
          data = await generateLeaveReport(selectedReport.id, params);
          break;
        case 'payroll':
          data = await generatePayrollReport(selectedReport.id, params);
          break;
        case 'compliance':
          data = await generateComplianceReport(selectedReport.id, params);
          break;
        case 'analytics':
          data = await generateAnalyticsReport(selectedReport.id, params);
          break;
        default:
          throw new Error('Invalid category');
      }

      setReportData(data);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  // Report generation functions
  const generateWorkforceReport = async (reportId, params) => {
    switch (reportId) {
      case 'headcount-summary':
      case 'employee-list':
      case 'demographics':
      case 'department-distribution':
        const response = await employeeAPI.getAll(params);
        return processWorkforceData(reportId, response.employees || []);

      case 'new-joiners':
      case 'separations':
        // Would filter employees by date_of_joining or separation_date
        const allEmps = await employeeAPI.getAll(params);
        return processWorkforceData(reportId, allEmps.employees || []);

      default:
        throw new Error('Unknown report type');
    }
  };

  const generateAttendanceReport = async (reportId, params) => {
    // Mock implementation - replace with actual API calls
    return {
      reportName: selectedReport.name,
      generated: new Date().toISOString(),
      data: [],
      summary: { totalRecords: 0 }
    };
  };

  const generateLeaveReport = async (reportId, params) => {
    // Mock implementation - replace with actual API calls
    return {
      reportName: selectedReport.name,
      generated: new Date().toISOString(),
      data: [],
      summary: { totalRecords: 0 }
    };
  };

  const generatePayrollReport = async (reportId, params) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.establishment_id) queryParams.append('establishment_id', params.establishment_id);
      if (params.month) queryParams.append('month', params.month);
      if (params.year) queryParams.append('year', params.year);

      const response = await payrollAPI.getMonthly(Object.fromEntries(queryParams));
      return {
        reportName: selectedReport.name,
        generated: new Date().toISOString(),
        data: response.payrollRecords || [],
        summary: response.summary || {}
      };
    } catch (error) {
      console.error('Error:', error);
      return {
        reportName: selectedReport.name,
        generated: new Date().toISOString(),
        data: [],
        summary: {}
      };
    }
  };

  const generateComplianceReport = async (reportId, params) => {
    // Mock implementation
    return {
      reportName: selectedReport.name,
      generated: new Date().toISOString(),
      data: [],
      summary: { totalRecords: 0 }
    };
  };

  const generateAnalyticsReport = async (reportId, params) => {
    // Mock implementation
    return {
      reportName: selectedReport.name,
      generated: new Date().toISOString(),
      data: [],
      summary: { totalRecords: 0 }
    };
  };

  const processWorkforceData = (reportId, employees) => {
    const data = employees.map(emp => ({
      employee_id: emp.employee_id,
      name: `${emp.first_name} ${emp.last_name}`,
      department: emp.department,
      designation: emp.designation,
      date_of_joining: emp.date_of_joining,
      status: emp.is_active ? 'Active' : 'Inactive',
      gender: emp.gender,
      email: emp.email,
      phone: emp.phone_number
    }));

    return {
      reportName: selectedReport.name,
      generated: new Date().toISOString(),
      data,
      summary: {
        totalEmployees: employees.length,
        active: employees.filter(e => e.is_active).length,
        inactive: employees.filter(e => !e.is_active).length
      }
    };
  };

  const handleExport = async (format) => {
    if (!reportData || !reportData.data) {
      alert('No data to export');
      return;
    }

    try {
      switch (format) {
        case 'excel':
          exportToExcel();
          break;
        case 'csv':
          exportToCSV();
          break;
        case 'pdf':
          exportToPDF();
          break;
        default:
          alert('Export format not supported');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting report: ' + error.message);
    }
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(reportData.data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');

    const filename = `${selectedReport.name.replace(/\s+/g, '_')}_${selectedYear}_${monthNames[selectedMonth - 1]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const exportToCSV = () => {
    const ws = XLSX.utils.json_to_sheet(reportData.data);
    const csv = XLSX.utils.sheet_to_csv(ws);

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedReport.name.replace(/\s+/g, '_')}_${selectedYear}_${monthNames[selectedMonth - 1]}.csv`;
    a.click();
  };

  const exportToPDF = () => {
    alert('PDF export will be implemented with jsPDF library');
    // TODO: Implement PDF export
  };

  const CategoryCard = ({ category, isSelected, onClick }) => (
    <div
      onClick={onClick}
      style={{
        padding: '1.5rem',
        background: isSelected ? `${category.color}15` : 'var(--theme-background)',
        border: `2px solid ${isSelected ? category.color : 'rgba(0,0,0,0.1)'}`,
        borderRadius: '0.75rem',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        transform: isSelected ? 'scale(1.02)' : 'scale(1)'
      }}
      onMouseEnter={(e) => !isSelected && (e.currentTarget.style.borderColor = category.color)}
      onMouseLeave={(e) => !isSelected && (e.currentTarget.style.borderColor = 'rgba(0,0,0,0.1)')}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
        <div style={{
          padding: '0.75rem',
          background: `${category.color}20`,
          borderRadius: '0.5rem'
        }}>
          <category.icon size={24} color={category.color} />
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: 'var(--theme-text)',
            marginBottom: '0.25rem'
          }}>
            {category.name}
          </h3>
          <p style={{
            fontSize: '0.875rem',
            color: '#6b7280',
            margin: 0
          }}>
            {category.description}
          </p>
        </div>
        {isSelected && (
          <CheckCircle2 size={20} color={category.color} />
        )}
      </div>
    </div>
  );

  const ReportCard = ({ report, isSelected, onClick }) => {
    const [isHovered, setIsHovered] = React.useState(false);

    return (
      <div
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          padding: '1rem 1.25rem',
          background: isSelected ? '#eff6ff' : isHovered ? '#ffffff' : 'var(--theme-background)',
          border: `2px solid ${isSelected ? '#3b82f6' : isHovered ? '#3b82f6' : 'rgba(0,0,0,0.1)'}`,
          borderRadius: '0.5rem',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
          boxShadow: isHovered ? '0 4px 12px rgba(59, 130, 246, 0.15)' : 'none'
        }}
      >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '0.875rem',
            fontWeight: '600',
            color: 'var(--theme-text)',
            marginBottom: '0.25rem'
          }}>
            {report.name}
          </div>
          <div style={{
            fontSize: '0.75rem',
            color: '#6b7280'
          }}>
            {report.description}
          </div>
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginTop: '0.5rem',
            flexWrap: 'wrap'
          }}>
            {report.exportFormats.map(format => (
              <span key={format} style={{
                padding: '0.125rem 0.5rem',
                background: 'var(--theme-surfaceHover)',
                borderRadius: '0.25rem',
                fontSize: '0.7rem',
                color: '#6b7280',
                textTransform: 'uppercase'
              }}>
                {format}
              </span>
            ))}
          </div>
        </div>
        {isSelected && <Check size={18} color="#3b82f6" />}
      </div>
    </div>
    );
  };

  if (loading) {
    return (
      <div className="update-employee-container">
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          <RefreshCw size={32} className="spinning" style={{ marginBottom: '1rem' }} />
          <p>Loading reports...</p>
        </div>
      </div>
    );
  }

  const currentCategory = reportCategories.find(c => c.id === selectedCategory);
  const availableReports = reportTypes[selectedCategory] || [];

  return (
    <div className="update-employee-container">
      <div className="update-employee-wrapper">
        {/* Header */}
        <div className="update-employee-header">
          <div>
            <h2 className="update-employee-title">
              <BarChart3 size={28} style={{ marginRight: '12px' }} />
              HR & Payroll Reports Center
            </h2>
            <p className="update-employee-subtitle">
              Generate comprehensive reports across workforce, payroll, compliance, and analytics
            </p>
          </div>
        </div>

        {/* Category Selection */}
        <div style={{ marginTop: '1.5rem' }}>
          {/* Step 1 Indicator */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1.5rem',
            padding: '1rem 1.5rem',
            background: '#eff6ff',
            border: '2px solid #3b82f6',
            borderRadius: '0.75rem',
            animation: selectedCategory ? 'none' : 'pulse 2s infinite'
          }}>
            <div style={{
              width: '2rem',
              height: '2rem',
              borderRadius: '50%',
              background: '#3b82f6',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '700',
              fontSize: '1rem',
              flexShrink: 0
            }}>
              1
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#3b82f6',
                marginBottom: '0.25rem'
              }}>
                Step 1: Choose a Category
              </div>
              <div style={{
                fontSize: '0.75rem',
                color: '#6b7280'
              }}>
                Select the type of report you want to generate
              </div>
            </div>
            {!selectedCategory && (
              <div style={{
                animation: 'bounce 1s infinite',
                color: '#3b82f6',
                fontSize: '1.5rem'
              }}>
                ↓
              </div>
            )}
            {selectedCategory && (
              <CheckCircle2 size={24} color="#10b981" />
            )}
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1rem'
          }}>
            {reportCategories.map((category, index) => (
              <div
                key={category.id}
                style={{
                  animation: `fadeInScale 0.4s ease-out ${index * 0.1}s backwards`
                }}
              >
                <CategoryCard
                  category={category}
                  isSelected={selectedCategory === category.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setSelectedReport(null);
                    setReportData(null);
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Report Type Selection */}
        {selectedCategory && (
          <div style={{ marginTop: '2rem' }} className="slide-up">
            {/* Step Indicator */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.5rem',
              padding: '1rem 1.5rem',
              background: `${currentCategory.color}10`,
              border: `2px solid ${currentCategory.color}40`,
              borderRadius: '0.75rem',
              animation: selectedReport ? 'none' : 'pulse 2s infinite'
            }}>
              <div style={{
                width: '2rem',
                height: '2rem',
                borderRadius: '50%',
                background: currentCategory.color,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                fontSize: '1rem',
                flexShrink: 0
              }}>
                2
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: currentCategory.color,
                  marginBottom: '0.25rem'
                }}>
                  Step 2: Select a Report Type
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: '#6b7280'
                }}>
                  Click on any report below to configure and generate
                </div>
              </div>
              <div style={{
                animation: 'bounce 1s infinite',
                color: currentCategory.color
              }}>
                ↓
              </div>
            </div>

            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: 'var(--theme-text)',
              marginBottom: '1rem'
            }}>
              <currentCategory.icon size={20} color={currentCategory.color} style={{ marginRight: '0.5rem', display: 'inline' }} />
              {currentCategory.name}
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '0.75rem'
            }}>
              {availableReports.map((report, index) => (
                <div
                  key={report.id}
                  style={{
                    animation: `slideInUp 0.3s ease-out ${index * 0.05}s backwards`
                  }}
                >
                  <ReportCard
                    report={report}
                    isSelected={selectedReport?.id === report.id}
                    onClick={() => {
                      setSelectedReport(report);
                      setReportData(null);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters & Generation */}
        {selectedReport && (
          <div style={{ marginTop: '2rem' }} className="slide-up">
            {/* Step 3 Indicator */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.5rem',
              padding: '1rem 1.5rem',
              background: '#d1fae5',
              border: '2px solid #10b981',
              borderRadius: '0.75rem'
            }}>
              <div style={{
                width: '2rem',
                height: '2rem',
                borderRadius: '50%',
                background: '#10b981',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                fontSize: '1rem',
                flexShrink: 0
              }}>
                3
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#059669',
                  marginBottom: '0.25rem'
                }}>
                  Step 3: Configure & Generate
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: '#6b7280'
                }}>
                  Set parameters and click Generate to create your report
                </div>
              </div>
            </div>

            <div style={{
              background: 'var(--theme-background)',
              border: '1.5px solid rgba(26, 115, 232, 0.2)',
              borderRadius: '0.75rem',
              padding: '1.5rem'
            }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: 'var(--theme-text)',
                marginBottom: '1rem'
              }}>
                <Filter size={18} style={{ marginRight: '0.5rem', display: 'inline' }} />
                Report Parameters
              </h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                {/* Establishment Filter — hidden for ClientAdmin */}
                {selectedReport.fields.includes('organization') && !lockedEstId && (
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Organization
                    </label>
                    <div className="update-employee-dropdown">
                      <select
                        value={selectedEstablishment}
                        onChange={(e) => setSelectedEstablishment(e.target.value)}
                        className="update-employee-select"
                      >
                        <option value="all">All Organizations</option>
                        {establishments.map(est => (
                          <option key={est.id} value={est.id}>{est.name}</option>
                        ))}
                      </select>
                      <ChevronDown className="update-employee-chevron" size={18} />
                    </div>
                  </div>
                )}

                {/* Department Filter */}
                {selectedReport.fields.includes('department') && (
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Department
                    </label>
                    <div className="update-employee-dropdown">
                      <select
                        value={selectedDepartment}
                        onChange={(e) => setSelectedDepartment(e.target.value)}
                        className="update-employee-select"
                        disabled={selectedEstablishment === 'all'}
                      >
                        <option value="all">All Departments</option>
                        {departments.map(dept => (
                          <option key={dept} value={dept}>{dept}</option>
                        ))}
                      </select>
                      <ChevronDown className="update-employee-chevron" size={18} />
                    </div>
                  </div>
                )}

                {/* Month Filter */}
                {selectedReport.fields.includes('month') && (
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Month
                    </label>
                    <div className="update-employee-dropdown">
                      <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                        className="update-employee-select"
                      >
                        {monthNames.map((month, idx) => (
                          <option key={idx} value={idx + 1}>{month}</option>
                        ))}
                      </select>
                      <ChevronDown className="update-employee-chevron" size={18} />
                    </div>
                  </div>
                )}

                {/* Year Filter */}
                {(selectedReport.fields.includes('year') || selectedReport.fields.includes('month')) && (
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Year
                    </label>
                    <div className="update-employee-dropdown">
                      <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        className="update-employee-select"
                      >
                        {years.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                      <ChevronDown className="update-employee-chevron" size={18} />
                    </div>
                  </div>
                )}

                {/* Date Range Filter */}
                {selectedReport.fields.includes('dateRange') && (
                  <>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}>
                        Start Date
                      </label>
                      <DateInput
                        value={dateRange.start}
                        onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        className="update-employee-select"
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}>
                        End Date
                      </label>
                      <DateInput
                        value={dateRange.end}
                        onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        className="update-employee-select"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerateReport}
                disabled={generating}
                className="update-employee-btn-create"
                style={{ width: '100%' }}
              >
                {generating ? (
                  <>
                    <RefreshCw size={16} className="spinning" />
                    Generating Report...
                  </>
                ) : (
                  <>
                    <FileText size={16} />
                    Generate {selectedReport.name}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Report Results */}
        {reportData && (
          <div style={{ marginTop: '2rem' }}>
            <div style={{
              background: '#f0fdf4',
              border: '1.5px solid #10b981',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#059669',
                    marginBottom: '0.5rem'
                  }}>
                    <CheckCircle2 size={20} style={{ marginRight: '0.5rem', display: 'inline' }} />
                    Report Generated Successfully
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#065f46', margin: 0 }}>
                    {reportData.reportName} • Generated on {new Date(reportData.generated).toLocaleString()}
                  </p>
                  {reportData.summary && (
                    <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#047857' }}>
                      {reportData.summary.totalEmployees && (
                        <span>Total Records: {reportData.summary.totalEmployees} | </span>
                      )}
                      {reportData.summary.active !== undefined && (
                        <span>Active: {reportData.summary.active} | </span>
                      )}
                      {reportData.summary.inactive !== undefined && (
                        <span>Inactive: {reportData.summary.inactive}</span>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {selectedReport.exportFormats.includes('excel') && (
                    <button
                      onClick={() => handleExport('excel')}
                      className="update-employee-btn-create"
                      style={{ minWidth: 'auto', padding: '0.5rem 1rem' }}
                    >
                      <FileSpreadsheet size={16} />
                      Excel
                    </button>
                  )}
                  {selectedReport.exportFormats.includes('pdf') && (
                    <button
                      onClick={() => handleExport('pdf')}
                      className="update-employee-btn-create"
                      style={{ minWidth: 'auto', padding: '0.5rem 1rem', background: '#ef4444' }}
                    >
                      <FileDown size={16} />
                      PDF
                    </button>
                  )}
                  {selectedReport.exportFormats.includes('csv') && (
                    <button
                      onClick={() => handleExport('csv')}
                      className="update-employee-btn-create"
                      style={{ minWidth: 'auto', padding: '0.5rem 1rem', background: '#8b5cf6' }}
                    >
                      <FileText size={16} />
                      CSV
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Data Preview */}
            {reportData.data && reportData.data.length > 0 && (
              <div style={{
                background: 'var(--theme-background)',
                border: '1.5px solid rgba(0,0,0,0.1)',
                borderRadius: '0.75rem',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '1rem 1.5rem',
                  background: 'var(--theme-surface)',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', margin: 0, color: 'var(--theme-text)' }}>
                    Data Preview ({reportData.data.length} records)
                  </h4>
                </div>
                <div style={{ overflowX: 'auto', maxHeight: '500px' }}>
                  <table style={{ width: '100%', fontSize: '0.875rem' }}>
                    <thead style={{ background: 'var(--theme-surface)', position: 'sticky', top: 0 }}>
                      <tr>
                        {Object.keys(reportData.data[0]).map(key => (
                          <th key={key} style={{
                            padding: '0.75rem 1rem',
                            textAlign: 'left',
                            fontWeight: '600',
                            color: '#374151',
                            borderBottom: '1px solid #e5e7eb'
                          }}>
                            {key.replace(/_/g, ' ').toUpperCase()}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.data.slice(0, 50).map((row, idx) => (
                        <tr key={idx} style={{
                          borderBottom: '1px solid #f8f9fa'
                        }}>
                          {Object.values(row).map((val, vidx) => (
                            <td key={vidx} style={{
                              padding: '0.75rem 1rem',
                              color: '#1f2937'
                            }}>
                              {val || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {reportData.data.length > 50 && (
                  <div style={{
                    padding: '1rem',
                    background: '#fffbeb',
                    borderTop: '1px solid #fbbf24',
                    fontSize: '0.875rem',
                    color: '#92400e',
                    textAlign: 'center'
                  }}>
                    <AlertCircle size={16} style={{ marginRight: '0.5rem', display: 'inline' }} />
                    Showing first 50 records. Export to view all {reportData.data.length} records.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <style jsx>{`
        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.9;
            transform: scale(1.02);
          }
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .slide-up {
          animation: slideInUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ReportsHub;
